/**
 * Services Data Registry
 * Central registry for all Inventive HQ services
 * Similar to categories.ts but for service offerings
 */

export interface Service {
  name: string;
  slug: string;
  description: string;
  category: string; // Service category slug
  tagline?: string;
  featured?: boolean; // Show on homepage
  icon?: string; // Path to icon image
  comingSoon?: boolean;
  parentSlug?: string; // For nested services (e.g., compliance sub-services)
  lastModified?: string; // ISO date string for sitemap lastmod
}

export interface ServiceCategory {
  name: string;
  slug: string;
  description: string;
  icon?: string;
}

// Service Categories
export const serviceCategories: ServiceCategory[] = [
  {
    name: "Security Services",
    slug: "security",
    description: "Virtual CISO, penetration testing, incident response, and security assessments",
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public"
  },
  {
    name: "Compliance Services",
    slug: "compliance-services",
    description: "HIPAA, NIST, SOC 2, PCI-DSS, and GDPR compliance consulting and certification",
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public"
  },
  {
    name: "Managed Services",
    slug: "managed-services",
    description: "24/7 managed security and IT services",
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public"
  },
  {
    name: "Cloud Solutions",
    slug: "cloud-solutions",
    description: "Cloud migration, optimization, and cost management",
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/caac7623-158b-47e2-500f-b49edbae5f00/public"
  },
  {
    name: "Professional Services",
    slug: "professional-services",
    description: "Custom software development, integrations, and IT consulting",
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public"
  }
];

// All Services
export const services: Service[] = [
  // Security Services
  {
    name: "Cybersecurity as a Service (vCISO)",
    slug: "vciso",
    description: "Cybersecurity as a Service (CaaS) delivering expert security leadership on-demand without the full-time cost",
    category: "security",
    tagline: "Cybersecurity as a Service",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/8b78f939-d2e6-472f-7199-c5ef3dfc7600/public",
    lastModified: "2026-01-31"
  },
  {
    name: "Small Business Cybersecurity",
    slug: "small-business-cybersecurity",
    description: "Small business cybersecurity solutions with enterprise-grade protection at SMB-friendly pricing",
    category: "security",
    tagline: "Enterprise Security for Growing Businesses",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    lastModified: "2026-01-31"
  },
  {
    name: "Vendor Risk Management",
    slug: "vendor-risk-management",
    description: "Assess and manage third-party vendor security risks",
    category: "security",
    tagline: "Secure Your Supply Chain",
    featured: false,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    lastModified: "2025-01-02"
  },
  {
    name: "Cybersecurity Compliance Services",
    slug: "compliance",
    description: "Cybersecurity compliance services for HIPAA, NIST, SOC 2, PCI-DSS, and GDPR",
    category: "compliance-services",
    tagline: "Cybersecurity Compliance Solutions",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    comingSoon: false,
    lastModified: "2026-01-31"
  },
  {
    name: "HIPAA Compliance",
    slug: "compliance/hipaa",
    description: "Healthcare compliance solutions including risk assessments, policy development, and ongoing HIPAA program management",
    category: "compliance-services",
    tagline: "Protect Patient Data, Meet HIPAA Requirements",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    parentSlug: "compliance",
    lastModified: "2025-01-02"
  },
  {
    name: "SOC 2 Compliance",
    slug: "compliance/soc2",
    description: "SOC 2 Type I and Type II compliance consulting for SaaS and technology companies",
    category: "compliance-services",
    tagline: "Build Trust with Enterprise Customers",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    parentSlug: "compliance",
    lastModified: "2025-01-02"
  },
  {
    name: "NIST Compliance",
    slug: "compliance/nist",
    description: "NIST Cybersecurity Framework implementation and NIST 800-171 compliance for government contractors",
    category: "compliance-services",
    tagline: "Meet Federal Security Standards",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    parentSlug: "compliance",
    lastModified: "2025-01-02"
  },
  {
    name: "PCI-DSS Compliance",
    slug: "compliance/pci-dss",
    description: "Payment Card Industry Data Security Standard compliance for businesses that process credit card payments",
    category: "compliance-services",
    tagline: "Secure Payment Processing",
    featured: false,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    comingSoon: false,
    parentSlug: "compliance",
    lastModified: "2025-01-02"
  },
  {
    name: "GDPR Compliance",
    slug: "compliance/gdpr",
    description: "General Data Protection Regulation compliance for businesses handling EU customer data",
    category: "compliance-services",
    tagline: "Protect EU Customer Privacy",
    featured: false,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    comingSoon: false,
    parentSlug: "compliance",
    lastModified: "2025-01-02"
  },
  {
    name: "AI Security Audit",
    slug: "ai-security-audit",
    description: "Comprehensive audit of AI tool usage, data exposure risks, and policy gaps with actionable remediation plan",
    category: "security",
    tagline: "Your Devs Are Using AI. Is Your IP Leaking?",
    featured: false,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    lastModified: "2026-02-25"
  },

  // Managed Services
  {
    name: "24/7 Detection and Response",
    slug: "24-7-detection-and-response",
    description: "Round-the-clock threat detection and response powered by CrowdStrike Complete",
    category: "managed-services",
    tagline: "Relentless Protection with CrowdStrike",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    lastModified: "2025-01-02"
  },
  {
    name: "Backup & Disaster Recovery",
    slug: "backup-and-ransomware-defense",
    description: "Comprehensive data protection and business continuity planning with ransomware defense",
    category: "managed-services",
    tagline: "Protect Your Business from Data Loss",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/caac7623-158b-47e2-500f-b49edbae5f00/public",
    lastModified: "2025-01-02"
  },
  {
    name: "Incident Response Planning",
    slug: "incident-response",
    description: "Prepare for cyber incidents, respond 10x faster, and meet compliance requirements with enterprise-level IR planning",
    category: "security",
    tagline: "Get Enterprise-Level IR On a Small Business Budget",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    lastModified: "2025-01-02"
  },
  {
    name: "Vulnerability Management",
    slug: "vulnerability-management",
    description: "Proactive vulnerability scanning and remediation to protect your infrastructure",
    category: "managed-services",
    tagline: "Proactive Protection, Superior Security",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    lastModified: "2025-01-02"
  },

  // Cloud Solutions
  {
    name: "Productivity Suite Management",
    slug: "productivity-suite-management",
    description: "Expert setup, security, migration, and ongoing management for Google Workspace and Microsoft 365",
    category: "cloud-solutions",
    tagline: "Google Workspace & Microsoft 365 Experts",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/caac7623-158b-47e2-500f-b49edbae5f00/public",
    lastModified: "2026-01-18"
  },
  {
    name: "Cloud Infrastructure Management & Optimization",
    slug: "cloud-infrastructure-management",
    description: "Multi-cloud architecture, migration, FinOps cost optimization, and 24/7 managed services for AWS, Azure, and Google Cloud",
    category: "cloud-solutions",
    tagline: "AWS, Azure & Google Cloud — Managed & Optimized",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/caac7623-158b-47e2-500f-b49edbae5f00/public",
    lastModified: "2026-05-27"
  },

  // Security Services
  {
    name: "Penetration Testing",
    slug: "penetration-testing",
    description: "Comprehensive penetration testing to identify security vulnerabilities before attackers do",
    category: "security",
    tagline: "Don't Wait for a Breach to Discover Weaknesses",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    lastModified: "2025-01-02"
  },
  {
    name: "Security Awareness Training",
    slug: "security-awareness",
    description: "Comprehensive employee security training and phishing simulation programs",
    category: "security",
    tagline: "Turn Employees Into Your Strongest Defense",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/8b78f939-d2e6-472f-7199-c5ef3dfc7600/public",
    lastModified: "2025-01-02"
  },

  // Professional Services
  {
    name: "Secure MVP",
    slug: "secure-mvp",
    description: "Productized MVP development for founders. Production-ready app shipped in 6 weeks for a fixed $20,000 — built secure from day one by someone who's shipped 9 SaaS apps and knows how attackers think",
    category: "professional-services",
    tagline: "Ship your MVP in 6 weeks. Fixed price. Secure by default.",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    lastModified: "2026-05-27"
  },
  {
    name: "Software Development",
    slug: "software-development",
    description: "Custom software development, system integrations, and automation solutions tailored to your business needs",
    category: "professional-services",
    tagline: "Build Solutions That Scale",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    lastModified: "2025-01-02"
  },
  {
    name: "DevOps Consulting Services",
    slug: "devops-consulting",
    description: "DevOps consulting services for CI/CD pipelines, infrastructure automation, Kubernetes, and cloud-native transformation",
    category: "professional-services",
    tagline: "Accelerate Delivery, Reduce Risk",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    lastModified: "2026-01-31"
  },
  {
    name: "DevSecOps Services",
    slug: "devsecops",
    description: "DevSecOps consulting and managed services for shift-left security, secure SDLC, and automated security in CI/CD pipelines",
    category: "security",
    tagline: "Security Built Into Every Deployment",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    lastModified: "2026-01-31"
  }
];

// Helper functions
export function getAllServices(): Service[] {
  return services;
}

export function getServiceBySlug(slug: string): Service | undefined {
  return services.find(service => service.slug === slug);
}

export function getServicesByCategory(categorySlug: string): Service[] {
  return services.filter(service => service.category === categorySlug);
}

export function getFeaturedServices(): Service[] {
  return services.filter(service => service.featured && !service.comingSoon);
}

export function getCategoryBySlug(slug: string): ServiceCategory | undefined {
  return serviceCategories.find(category => category.slug === slug);
}

export function getAllServiceCategories(): ServiceCategory[] {
  return serviceCategories;
}

// Get related services (same category, exclude current)
export function getRelatedServices(currentSlug: string, limit: number = 3): Service[] {
  const currentService = getServiceBySlug(currentSlug);
  if (!currentService) return [];

  const relatedInCategory = services
    .filter(service =>
      service.category === currentService.category &&
      service.slug !== currentSlug &&
      !service.comingSoon
    )
    .slice(0, limit);

  // If not enough in same category, add featured services from other categories
  if (relatedInCategory.length < limit) {
    const additionalServices = services
      .filter(service =>
        service.slug !== currentSlug &&
        !relatedInCategory.find(s => s.slug === service.slug) &&
        service.featured &&
        !service.comingSoon
      )
      .slice(0, limit - relatedInCategory.length);

    return [...relatedInCategory, ...additionalServices];
  }

  return relatedInCategory;
}

// Get child services (nested under a parent service)
export function getChildServices(parentSlug: string): Service[] {
  return services.filter(service => service.parentSlug === parentSlug);
}

// Get all child services including coming soon
export function getAllChildServices(parentSlug: string): Service[] {
  return services.filter(service => service.parentSlug === parentSlug);
}
